import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  Animated,
  ScrollView,
} from "react-native";
import { useState, useEffect, useRef } from "react";
import { Feather, Ionicons, FontAwesome5, Entypo } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import OnboardingStepIndicator from "../components/OnboardingStepIndicator";
import LanguagePicker from "../components/LanguagePicker";
import CookiePanel from "../components/CookiePanel";
import ClarityManager from "../../Classes/ClarityManager";
import MetaPixelManager from "../../Classes/MetaPixelManager";
import {
  NameStep,
  AthletesCountStep,
  AthleteTypeStep,
  FeaturesStep,
  LogoStep,
  PreferencesStep,
} from "../components/OnboardingSteps";
import { doc, getDoc } from "firebase/firestore";
import db, { app, auth, storage } from "../../Firestore/FirestoreConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../AuthContext";
import { useAcquisition } from "../AcquisitionContext";
import { useTranslation } from "react-i18next";
import {
  CLARITY_PROJECT_ID,
  META_PIXEL_ID,
} from "../../../Resources/Constants";

const generateMetaEventId = () =>
  `meta_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
const FUNCTIONS_REGION = "europe-west1";

const buildCaptureSignupCountryUrl = () => {
  const projectId = app?.options?.projectId;
  if (!projectId) {
    return null;
  }

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const shouldUseFunctionsEmulator =
    isLocalhost &&
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("useFunctionsEmulator") ===
      "1" ||
      new URLSearchParams(window.location.search).get("useFunctionsEmulator") ===
        "true" ||
      window.localStorage?.getItem("useFunctionsEmulator") === "true");

  if (shouldUseFunctionsEmulator) {
    return `http://localhost:5001/${projectId}/${FUNCTIONS_REGION}/captureSignupCountry`;
  }

  return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/captureSignupCountry`;
};

/**
 * Onboarding view component
 * Shown to authenticated users who don't have coach data yet
 */
const OnboardingView = ({ coach }) => {
  const { user, completeOnboarding } = useAuth();
  const {
    acquisitionRef,
    fbclid,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
  } = useAcquisition();
  const [currentStep, setCurrentStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Track OnboardingView visibility
  const [showCookiePanel, setShowCookiePanel] = useState(false); // Track cookie panel visibility
  const [cookiesAccepted, setCookiesAccepted] = useState(false); // Track if cookies were accepted
  const completeRegistrationEventIdRef = useRef(generateMetaEventId());
  const powerlifterRegistrationSuccessEventIdRef = useRef(generateMetaEventId());
  const completeRegistrationEventId = completeRegistrationEventIdRef.current;
  const powerlifterRegistrationSuccessEventId =
    powerlifterRegistrationSuccessEventIdRef.current;
  const totalSteps = 6;
  const { t } = useTranslation();

  // Ref for the brand text to set translate attribute
  const brandTextRef = useRef(null);

  // Animation values
  const logoPosition = useRef(new Animated.Value(0)).current;
  const textOpacity = useRef(new Animated.Value(1)).current;
  const backButtonOpacity = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(0)).current;

  // Step 0: Name
  const [name, setName] = useState("");

  // Step 1: Athletes count
  const [athletesRange, setAthletesRange] = useState("");

  // Step 2: Athlete types
  const [athleteTypes, setAthleteTypes] = useState([]);

  // Step 3: Features
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  // Step 4: Logo
  const [logoUri, setLogoUri] = useState(null);

  // Step 5: Preferences
  const [preferences, setPreferences] = useState({
    rir_or_rpe: false,
    exercise_unit: false,
    show_athlete_exercise_stats: true,
    show_set_priority: false,
  });

  // Initial fade-in animation on mount
  useEffect(() => {
    setIsVisible(true); // Show panel when component mounts
    contentOpacity.setValue(0);
    Animated.timing(contentOpacity, {
      toValue: 1,
      duration: 250,
      useNativeDriver: true,
    }).start();

    // Show cookie panel after 1 second delay
    const cookiePanelTimer = setTimeout(() => {
      setShowCookiePanel(true);
    }, 2000);

    // Set translate="no" on brand text element
    if (brandTextRef.current) {
      const node = brandTextRef.current;
      if (node && node.setAttribute) {
        node.setAttribute("translate", "no");
        node.classList?.add("notranslate");
      }
    }

    // Initialize Clarity tracking for this entire session
    // Store project ID but DON'T start recording yet - wait for consent
    // Recording only starts when user accepts cookies
    if (
      CLARITY_PROJECT_ID &&
      CLARITY_PROJECT_ID !== "YOUR_CLARITY_PROJECT_ID"
    ) {
      ClarityManager.init(CLARITY_PROJECT_ID);

      // Tag this session with useful context (will be queued until consent)
      ClarityManager.setTag("view", "onboarding");
      ClarityManager.setTag("session_started_with", "onboarding");
      if (user?.uid) {
        ClarityManager.identify(user.uid);
      }
    }

    // Initialize Meta Pixel tracking for this entire session
    // Store pixel ID but DON'T start tracking yet - wait for consent
    // Tracking only starts when user accepts cookies
    if (META_PIXEL_ID && META_PIXEL_ID !== "YOUR_META_PIXEL_ID") {
      MetaPixelManager.init(META_PIXEL_ID);

      // Queue custom events (will be sent after consent)
      MetaPixelManager.trackCustomEvent("OnboardingStarted");
    }

    // Cleanup: hide cookie panel when component unmounts
    // Note: We DON'T stop Clarity/Meta Pixel tracking here - they continue for the whole session
    // (but only if user accepted cookies)
    return () => {
      setIsVisible(false);
      clearTimeout(cookiePanelTimer); // Clear timer if component unmounts
      console.log(
        "[OnboardingView] Unmounting - Clarity and Meta Pixel will continue tracking if consent was granted"
      );
    };
  }, []);

  // Initialize preferences based on athlete types when transitioning to PreferencesStep
  useEffect(() => {
    if (currentStep === 5 && !athleteTypes.includes("powerlifters")) {
      setPreferences((prev) => ({
        ...prev,
        rir_or_rpe: true,
        show_set_priority: false,
      }));
    }

    // Track step changes in Clarity and Meta Pixel
    ClarityManager.event(`onboarding_step_${currentStep}`);
    MetaPixelManager.trackCustomEvent(`OnboardingStep${currentStep}`);
  }, [currentStep, athleteTypes]);

  // Handle cookie acceptance
  const handleCookieAccept = () => {
    console.log("[OnboardingView] User accepted cookies");

    // Grant consent to both analytics services
    ClarityManager.grantConsent();
    ClarityManager.event("cookies_accepted");

    MetaPixelManager.grantConsent();
    MetaPixelManager.trackCustomEvent("CookiesAccepted");

    // Track that cookies were accepted
    setCookiesAccepted(true);

    // Hide cookie panel
    setShowCookiePanel(false);
  };

  // Handle cookie rejection
  const handleCookieReject = () => {
    console.log("[OnboardingView] User rejected cookies");

    // Deny consent to both analytics services
    ClarityManager.denyConsent();
    MetaPixelManager.denyConsent();
    // Hide cookie panel
    setShowCookiePanel(false);
  };

  const toggleAthleteType = (type) => {
    setAthleteTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleFeature = (featureId) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((f) => f !== featureId)
        : [...prev, featureId]
    );
  };

  const handlePreferenceChange = (prefId, value) => {
    setPreferences((prev) => ({ ...prev, [prefId]: value }));
  };

  const captureSignupCountry = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn(
          "[OnboardingView] Skipping country capture: auth.currentUser is missing"
        );
        return;
      }

      const functionUrl = buildCaptureSignupCountryUrl();
      if (!functionUrl) {
        console.warn(
          "[OnboardingView] Skipping country capture: function URL unavailable"
        );
        return;
      }

      const idToken = await currentUser.getIdToken();
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok && response.status !== 204) {
        console.warn(
          `[OnboardingView] captureSignupCountry returned HTTP ${response.status}`
        );
      }
    } catch (error) {
      console.error("[OnboardingView] captureSignupCountry failed:", error);
    }
  };

  const handleFinishOnboarding = async (language, screenSize) => {
    if (!user?.uid) {
      console.error("[OnboardingView] No user ID available");
      return;
    }

    // Record ALL onboarding data at once when user finishes
    recordAllOnboardingData(language);

    setIsSaving(true);

    try {
      const isPowerlifterSignup = athleteTypes.includes("powerlifters");

      // Temporarily check that there is no coach document to make sure we don't create duplicates
      const coachDocRef = doc(db, "coaches", user.uid);
      const coachDoc = await getDoc(coachDocRef);

      if (!coachDoc.exists()) {
        console.log(
          "[OnboardingView] Marking onboarding as complete, will initialize coach"
        );

        // Mark onboarding as complete BEFORE initializing coach
        // This will trigger redirect to index.js

        // Detect in-app browser if no explicit ref parameter
        let finalAcquisitionRef = acquisitionRef;
        if (!finalAcquisitionRef && typeof navigator !== "undefined") {
          const userAgent = navigator.userAgent;
          if (userAgent.includes("Instagram")) {
            finalAcquisitionRef = "browser-instagram";
          } else if (
            userAgent.includes("FBAN") ||
            userAgent.includes("FBAV") ||
            userAgent.includes("FB_IAB")
          ) {
            finalAcquisitionRef = "browser-facebook";
          } else if (userAgent.includes("musical_ly")) {
            finalAcquisitionRef = "browser-tiktok";
          } else if (userAgent.includes("LinkedIn")) {
            finalAcquisitionRef = "browser-linkedin";
          } else if (userAgent.includes("Twitter")) {
            finalAcquisitionRef = "browser-twitter";
          } else if (userAgent.includes("Telegram")) {
            finalAcquisitionRef = "browser-telegram";
          } else if (userAgent.includes("WhatsApp")) {
            finalAcquisitionRef = "browser-whatsapp";
          } else if (userAgent.includes("Reddit")) {
            finalAcquisitionRef = "browser-reddit";
          }
        }

        // Now initialize the coach (this will be displayed with loading indicator in index.js)
        console.log("[OnboardingView] Initializing coach with data:", {
          id: user.uid,
          name,
          email: user.email,
          logoUri,
          preferences,
          language,
          selectedFeatures,
        });

        await coach.initializeCoach(
          user.uid,
          name,
          user.email,
          logoUri,
          preferences,
          language,
          selectedFeatures,
          athletesRange,
          athleteTypes,
          completeOnboarding,
          screenSize,
          finalAcquisitionRef,
          fbclid,
          utmSource,
          utmMedium,
          utmCampaign,
          utmContent,
          utmAdset,
          utmTerm,
          cookiesAccepted,
          completeRegistrationEventId,
          powerlifterRegistrationSuccessEventId
        );

        // coach.initialLoading = false;
        console.log("[OnboardingView] Coach initialization complete");
        captureSignupCountry();

        // Track successful onboarding completion
        ClarityManager.event("onboarding_completed");
        MetaPixelManager.trackEvent(
          "CompleteRegistration",
          {
            content_name: "Coach Onboarding",
            status: "completed",
          },
          { eventId: completeRegistrationEventId }
        );

        if (cookiesAccepted && isPowerlifterSignup) {
          MetaPixelManager.trackCustomEvent(
            "PowerlifterRegistrationSuccess",
            {
              content_name: "Coach Onboarding",
              content_category: "onboarding",
              athlete_type: "powerlifters",
              status: "completed",
            },
            { eventId: powerlifterRegistrationSuccessEventId }
          );
        }
      } else {
        console.error(
          "[OnboardingView] Coach document already exists, skipping creation of demo data"
        );
        completeOnboarding();
      }

      setIsSaving(false);
    } catch (error) {
      console.error("[OnboardingView] Error saving onboarding data:", error);
      ClarityManager.event("onboarding_error");
      MetaPixelManager.trackCustomEvent("OnboardingError");
      setIsSaving(false);
      coach.initialLoading = false;
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      transitionToStep(currentStep - 1);
    }
  };

  const transitionToStep = (nextStep) => {
    // Start transition by fading out current content
    setIsTransitioning(true);

    Animated.timing(contentOpacity, {
      toValue: 0,
      duration: 100,
      useNativeDriver: true,
    }).start(() => {
      // Wait 1 second with white screen
      setTimeout(() => {
        // Change step
        setCurrentStep(nextStep);

        // Fade in new content
        Animated.timing(contentOpacity, {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }).start(() => {
          setIsTransitioning(false);
        });
      }, 300);
    });
  };

  const recordAllOnboardingData = (language) => {
    // Record all onboarding steps at once

    // Step 0: Name
    if (name) {
      ClarityManager.setTag("has_name", "true");
    }

    // Step 1: Athletes count
    if (athletesRange) {
      ClarityManager.setTag("athletes_range", athletesRange);
    }

    // Step 2: Athlete types
    if (athleteTypes.length > 0) {
      ClarityManager.setTag("athlete_types", athleteTypes.join(","));
    }

    // Step 3: Features
    if (selectedFeatures.length > 0) {
      ClarityManager.setTag("selected_features", selectedFeatures.join(","));
    }

    // Step 4: Logo
    ClarityManager.setTag("has_logo", logoUri ? "true" : "false");

    // Step 5: Preferences
    ClarityManager.setTag("rir_or_rpe", preferences.rir_or_rpe ? "rpe" : "rir");
    ClarityManager.setTag(
      "exercise_unit",
      preferences.exercise_unit ? "lbs" : "kg"
    );
    ClarityManager.setTag(
      "show_athlete_stats",
      preferences.show_athlete_exercise_stats.toString()
    );
    ClarityManager.setTag(
      "show_set_priority",
      preferences.show_set_priority.toString()
    );

    // Completion
    ClarityManager.event("onboarding_completed");
    ClarityManager.setTag("onboarding_language", language);
    ClarityManager.setTag("onboarding_completed", "true");
  };

  // Handle header transition animation
  useEffect(() => {
    if (currentStep === 0 && previousStep > 0) {
      // Transitioning back to step 0
      Animated.parallel([
        Animated.timing(logoPosition, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
        }),
        Animated.timing(textOpacity, {
          toValue: 1,
          duration: 300,
          delay: 100,
          useNativeDriver: true,
        }),
        Animated.timing(backButtonOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    } else if (currentStep > 0 && previousStep === 0) {
      // Transitioning from step 0 to step 1
      Animated.parallel([
        Animated.timing(textOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(logoPosition, {
          toValue: 1,
          duration: 400,
          delay: 100,
          useNativeDriver: true,
        }),
        Animated.timing(backButtonOpacity, {
          toValue: 1,
          duration: 300,
          delay: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
    setPreviousStep(currentStep);
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <NameStep
            value={name}
            onChange={setName}
            onContinue={() => transitionToStep(1)}
          />
        );
      case 1:
        return (
          <AthletesCountStep
            selectedRange={athletesRange}
            onSelect={setAthletesRange}
            onContinue={() => transitionToStep(2)}
          />
        );
      case 2:
        return (
          <AthleteTypeStep
            selectedTypes={athleteTypes}
            onToggle={toggleAthleteType}
            onContinue={() => transitionToStep(3)}
          />
        );
      case 3:
        return (
          <FeaturesStep
            selectedFeatures={selectedFeatures}
            athleteTypes={athleteTypes}
            onToggle={toggleFeature}
            onContinue={() => transitionToStep(4)}
          />
        );
      case 4:
        return (
          <LogoStep
            logoUri={logoUri}
            selectedFeatures={selectedFeatures}
            athleteTypes={athleteTypes}
            onUpload={setLogoUri}
            onContinue={() => transitionToStep(5)}
          />
        );
      case 5:
        return (
          <PreferencesStep
            preferences={preferences}
            athleteTypes={athleteTypes}
            onPreferenceChange={handlePreferenceChange}
            onFinish={handleFinishOnboarding}
            isSaving={isSaving}
          />
        );
      default:
        return null;
    }
  };

  // Calculate logo translation
  const logoTranslateX = logoPosition.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 82], // Moves logo to the right
  });

  return (
    <View style={styles.container}>
      {/* Header with animated transitions */}
      <View style={styles.header}>
        {/* Back button - fades in when not on step 0 */}
        <Animated.View
          style={[
            styles.backButtonContainer,
            {
              opacity: backButtonOpacity,
              pointerEvents: currentStep === 0 ? "none" : "auto",
            },
          ]}
        >
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Feather name="chevron-left" size={20} color={Colors.grayColor} />
            <Text style={styles.backText}>{t("back-string")}</Text>
          </TouchableOpacity>
          <View style={styles.separator} />
        </Animated.View>

        {/* Logo - moves to the right */}
        <Animated.Image
          source={require("../../../assets/BlackPng.png")}
          style={[
            styles.logo,
            {
              transform: [{ translateX: logoTranslateX }],
            },
          ]}
          resizeMode="contain"
        />

        {/* Brand text - fades out when leaving step 0 */}
        <Animated.Text
          ref={brandTextRef}
          style={[
            styles.brandText,
            {
              opacity: textOpacity,
              transform: [{ translateX: logoTranslateX }],
              marginLeft: 1,
            },
          ]}
        >
          EFORT COACH
        </Animated.Text>
      </View>

      {/* Main content area */}
      <View style={[styles.content, {}]}>
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingVertical: 24,
            alignItems: "center",
            justifyContent: "center",
          }}
          style={{ width: "100%", height: "100%" }}
          showsVerticalScrollIndicator={false}
        >
          <Animated.View
            style={{
              width: "100%",
              opacity: contentOpacity,
            }}
          >
            {renderStep()}
          </Animated.View>
        </ScrollView>
      </View>

      {/* Step indicator at bottom */}
      <View style={styles.footer}>
        <OnboardingStepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepPress={transitionToStep}
        />
      </View>

      {/* Language Picker - bottom left */}
      <View style={styles.languagePickerContainer}>
        <LanguagePicker showGlobe={false} color={Colors.grayColor} />
      </View>

      {/* 🧪 EXPERIMENT: Hide cookie panel when auto-consent is enabled */}
      {!ClarityManager.AUTO_CONSENT_EXPERIMENT && (
        <CookiePanel
          visible={isVisible && showCookiePanel}
          onAccept={handleCookieAccept}
          onReject={handleCookieReject}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.whiteColor,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 12,
    // paddingBottom: 12,
  },
  logo: {
    width: 38,
    height: 38,
    marginRight: 8,
  },
  brandText: {
    fontSize: 20,
    fontWeight: "700",
    color: Colors.blackColor,
    // marginLeft: 0,
  },
  backButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    position: "absolute",
    left: 16,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    cursor: "pointer",
  },
  backText: {
    fontSize: 14,
    color: Colors.grayColor,
  },
  separator: {
    width: 1,
    height: 20,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 12,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: "600",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 18,
    color: Colors.grayColor,
  },
  footer: {
    bottom: 40,
    alignSelf: "center",
    alignItems: "center",
    position: "absolute",
  },
  languagePickerContainer: {
    position: "absolute",
    bottom: 8,
    left: 8,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: 10,
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default OnboardingView;
